import { useState, useEffect, useRef, useCallback } from 'react';
import type { WebSocketMessage, WebSocketStatus } from '../types';

interface UseWebSocketOptions {
  url: string;
  autoConnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

interface UseWebSocketReturn {
  status: WebSocketStatus;
  messages: WebSocketMessage[];
  send: (message: unknown) => boolean;
  connect: () => void;
  disconnect: () => void;
  lastMessage: WebSocketMessage | null;
  latency: number;
}

export function useWebSocket({
  url,
  autoConnect = true,
  reconnectInterval = 3000,
  maxReconnectAttempts = 10,
  heartbeatInterval = 30000,
  onMessage,
  onConnect,
  onDisconnect,
  onError,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [latency, setLatency] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const latencyCheckRef = useRef<number>(0);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearHeartbeatTimer = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimer();
    clearHeartbeatTimer();
    reconnectAttemptsRef.current = maxReconnectAttempts + 1; // Prevent reconnection

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    setStatus('disconnected');
    onDisconnect?.();
  }, [clearReconnectTimer, clearHeartbeatTimer, maxReconnectAttempts, onDisconnect]);

  const connect = useCallback(() => {
    clearReconnectTimer();

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setStatus('connecting');
    reconnectAttemptsRef.current += 1;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        reconnectAttemptsRef.current = 0;
        onConnect?.();

        // Start heartbeat
        clearHeartbeatTimer();
        heartbeatTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            latencyCheckRef.current = Date.now();
            ws.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
          }
        }, heartbeatInterval);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketMessage;

          // Handle pong for latency
          if (data.type === 'pong' && latencyCheckRef.current > 0) {
            const roundTrip = Date.now() - latencyCheckRef.current;
            setLatency(roundTrip);
            latencyCheckRef.current = 0;
            return;
          }

          setLastMessage(data);
          setMessages((prev) => [...prev.slice(-999), data]);
          onMessage?.(data);
        } catch {
          // Handle non-JSON messages
          const msg: WebSocketMessage = {
            type: 'raw',
            payload: event.data,
            timestamp: new Date().toISOString(),
          };
          setLastMessage(msg);
          onMessage?.(msg);
        }
      };

      ws.onclose = () => {
        setStatus('disconnected');
        clearHeartbeatTimer();
        onDisconnect?.();

        // Attempt reconnection
        if (reconnectAttemptsRef.current <= maxReconnectAttempts) {
          setStatus('reconnecting');
          reconnectTimerRef.current = setTimeout(() => {
            connect();
          }, reconnectInterval * Math.min(reconnectAttemptsRef.current, 5));
        }
      };

      ws.onerror = (error) => {
        setStatus('error');
        onError?.(error);
      };
    } catch (error) {
      setStatus('error');
      console.error('WebSocket connection error:', error);
    }
  }, [url, heartbeatInterval, maxReconnectAttempts, reconnectInterval, clearHeartbeatTimer, clearReconnectTimer, onConnect, onDisconnect, onError, onMessage]);

  const send = useCallback((message: unknown): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (autoConnect) {
      reconnectAttemptsRef.current = 0;
      connect();
    }

    return () => {
      clearReconnectTimer();
      clearHeartbeatTimer();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [autoConnect, connect, clearReconnectTimer, clearHeartbeatTimer]);

  return {
    status,
    messages,
    send,
    connect,
    disconnect,
    lastMessage,
    latency,
  };
}
